import type { FC, PointerEvent, ReactNode } from 'react'
import type { Project } from '@/types'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ProjectImage } from '@/components/generals/project-image'

interface WorkListProps {
    projects: Project[]
}

// Archive list with a cover preview that follows the mouse (touch screens get inline thumbnails).
export const WorkList: FC<WorkListProps> = ({ projects }): ReactNode => {
    const previewRef = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState<number | null>(null)

    const movePreview = (event: PointerEvent<HTMLElement>) => {
        if (event.pointerType !== 'mouse') return

        previewRef.current?.style.setProperty('--cx', `${event.clientX}px`)
        previewRef.current?.style.setProperty('--cy', `${event.clientY}px`)
    }

    return (
        <div onPointerMove={movePreview} onPointerLeave={() => setActive(null)}>
            <ul className='border-t border-border'>
                {projects.map((project, index) => (
                    <li key={project.id} className='border-b border-border'>
                        <Link
                            href={`/work/${project.slug}`}
                            onPointerEnter={() => setActive(index)}
                            className='group grid grid-cols-[1fr_auto] items-center gap-x-5 gap-y-1 py-7 md:grid-cols-[3.5rem_minmax(0,1.6fr)_minmax(0,1fr)_5rem_auto] md:gap-x-8 md:py-10'
                        >
                            <span className='hidden font-mono text-xs text-muted-foreground md:block'>
                                {String(index + 1).padStart(2, '0')}
                            </span>

                            <div className='min-w-0'>
                                <h2
                                    data-mdlfy-cms={`projects:${project.id}:title`}
                                    className='text-3xl font-semibold tracking-tight transition-[translate,color] duration-500 group-hover:translate-x-2 group-hover:text-white md:text-5xl'
                                >
                                    {project.title}
                                </h2>

                                {project.tagline && (
                                    <p
                                        data-mdlfy-cms={`projects:${project.id}:tagline`}
                                        className='mt-2 line-clamp-2 max-w-xl text-sm text-muted-foreground md:text-base'
                                    >
                                        {project.tagline}
                                    </p>
                                )}
                            </div>

                            <div className='hidden text-muted-foreground md:block'>
                                {project.category && (
                                    <p data-mdlfy-cms={`projects:${project.id}:category`}>
                                        {project.category}
                                    </p>
                                )}
                            </div>

                            <div className='hidden font-mono text-sm text-muted-foreground md:block'>
                                {project.year && (
                                    <p data-mdlfy-cms={`projects:${project.id}:year`}>
                                        {project.year}
                                    </p>
                                )}
                            </div>

                            <span className='relative row-span-2 grid size-12 shrink-0 place-items-center overflow-hidden rounded-full border border-border transition-colors duration-300 group-hover:border-primary group-hover:bg-primary md:row-span-1 md:size-14'>
                                <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover:rotate-45' />
                            </span>

                            <div className='mt-4 flex items-center gap-4 md:hidden'>
                                <div className='relative aspect-[4/3] w-28 shrink-0 overflow-hidden rounded-lg border border-white/[0.06] bg-card'>
                                    <ProjectImage src={project.cover} alt={project.coverAlt ?? ''} sizes='112px' />
                                </div>

                                <div className='font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground'>
                                    {project.category && (
                                        <p data-mdlfy-cms={`projects:${project.id}:category`}>
                                            {project.category}
                                        </p>
                                    )}

                                    {project.year && (
                                        <p data-mdlfy-cms={`projects:${project.id}:year`} className='mt-1'>
                                            {project.year}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>

            <div
                ref={previewRef}
                aria-hidden='true'
                className={cn(
                    'cursor-follow pointer-events-none fixed left-0 top-0 z-40 hidden aspect-[4/3] w-[22rem] overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl transition-[opacity,scale] duration-300 pointer-fine:block',
                    active === null ? 'scale-90 opacity-0' : 'scale-100 opacity-100'
                )}
            >
                {projects.map((project, index) => (
                    <div
                        key={project.id}
                        className={cn('absolute inset-0 transition-opacity duration-500', index === active ? 'opacity-100' : 'opacity-0')}
                    >
                        <ProjectImage src={project.cover} alt='' sizes='352px' />
                    </div>
                ))}
            </div>
        </div>
    )
}
